/**
 * What the transcript page does once a tool page's transcript is there (SEO_BRIEF_09 sections 6.1
 * and 6.1b): summarize it, or write social captions from it.
 *
 * /youtube-summarizer and /video-summarizer say `summarize`, /caption-generator `captions` (the
 * row's `then`, src/formats/summary-tools.ts). The page takes a link (pages/landing.ts) or a file
 * (pages/tool.ts) the way /video-to-text does, and goes where it would go anyway, the job page or
 * the transcript page (`/t/{id}`), with the action on the URL:
 *
 *   /t/{id}?do=summarize
 *   /t/{id}?do=summarize&lang=es               a language picked beside the box
 *   /t/{id}?do=captions&platform=tiktok        the platform picked beside the box
 *
 * The summary and the captions are in the video's language unless the visitor picks another
 * ("the video's, or one you pick", 6.1 and 6.1b): the language picker's first choice, "Same
 * language as the video", is the empty value and adds nothing; any other adds `&lang=`.
 *
 * The URL is the whole hand-off, and nothing is stored anywhere. The parameters are written by
 * lib/auto-action.ts (`withAction`), the module the job page (`withAutoAction`, which carries them
 * on to the transcript as it carries `?download=`) and the transcript page (`autoActionFrom`,
 * which runs the action once) use too, so there is one spelling of them. A value that is not one
 * of ours is dropped here rather than put on the URL.
 */

import { withAction } from './auto-action.ts';
import { qs } from './dom.ts';
import { CAPTION_PLATFORMS, type CaptionPlatform, TARGET_CODE } from './usage.ts';

/** The two actions, as the row and the transcript page spell them. */
export const THEN_ACTIONS = ['summarize', 'captions'] as const;
export type ThenAction = (typeof THEN_ACTIONS)[number];

/** The caption generator's platform picker (tools/tool.html `data-caption-platform`). */
export const PLATFORM_PICKER = '[data-caption-platform]';
/** The language picker on all three pages (tools/tool.html `data-then-lang`). */
export const LANG_PICKER = '[data-then-lang]';

export function isThenAction(value: unknown): value is ThenAction {
  return typeof value === 'string' && (THEN_ACTIONS as readonly string[]).includes(value);
}

export function isCaptionPlatform(value: unknown): value is CaptionPlatform {
  return typeof value === 'string' && (CAPTION_PLATFORMS as readonly string[]).includes(value);
}

/** A language code as the transcript page takes one: `es`, `yue`, `zh-Hant` (lib/usage.ts). */
export function isThenLang(value: unknown): value is string {
  return typeof value === 'string' && TARGET_CODE.test(value);
}

/**
 * `url` with `?do={then}` set, `&platform=` for captions (the first platform when none usable
 * was picked) and `&lang=` when a language was picked (none for "Same language as the video"),
 * or `url` untouched when `then` is not one of the two actions.
 */
export function withThen(
  url: string,
  then: string | null | undefined,
  platform?: string | null,
  lang?: string | null,
): string {
  if (!isThenAction(then)) return url;
  const code = isThenLang(lang) ? lang : null;
  return withAction(
    url,
    then === 'captions'
      ? {
          kind: 'captions',
          platform: isCaptionPlatform(platform) ? platform : CAPTION_PLATFORMS[0],
          lang: code,
        }
      : { kind: 'summarize', lang: code },
  );
}

/** The platform picked beside the box, or null on a page without the picker. */
export function pickedPlatform(root: ParentNode = document): CaptionPlatform | null {
  const value = qs<HTMLSelectElement>(PLATFORM_PICKER, root)?.value;
  return isCaptionPlatform(value) ? value : null;
}

/**
 * The language picked beside the box, or null for "Same language as the video" (its value is
 * empty) and on a page without the picker.
 */
export function pickedLang(root: ParentNode = document): string | null {
  const value = qs<HTMLSelectElement>(LANG_PICKER, root)?.value;
  return isThenLang(value) ? value : null;
}
